import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Tabs, Tab, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, TextField, Select, MenuItem,
  IconButton, Chip, LinearProgress, Card, CardContent, Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../services/api';

const MONTHS = ['Січ', 'Лют', 'Бер', 'Кві', 'Тра', 'Чер', 'Лип', 'Сер', 'Вер', 'Жов', 'Лис', 'Гру'];
const MONTH_FULL = ['Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень', 'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];

const fmt = (v) => v == null ? '' : Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const fmtSign = (v, sign) => v == null || v == 0 ? '' : `${sign}${fmt(v)}`;

function StatCard({ label, value, color }) {
  return (
    <Card sx={{ flex: 1, minWidth: 140 }}>
      <CardContent sx={{ p: '10px !important' }}>
        <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
        <Typography variant="subtitle1" fontWeight={700} color={color || 'text.primary'} sx={{ fontSize: 15 }}>
          {fmt(value)} грн
        </Typography>
      </CardContent>
    </Card>
  );
}

// ─── Excel cell styles ────────────────────────────────────
const XL_BORDER = '1px solid #c6c6c6';
const XL_HEADER_BG = '#e8edf3';
const XL_EVEN_BG = '#f8f9fb';

const xlCell = {
  border: XL_BORDER,
  borderCollapse: 'collapse',
  px: '5px',
  py: '1px',
  fontSize: 12,
  lineHeight: '22px',
  height: 24,
  cursor: 'text',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  maxWidth: 0,
};

const inputSx = {
  '& .MuiInputBase-input': { fontSize: 12, py: '1px', px: '3px', height: '20px' },
  '& fieldset': { border: '2px solid #1565c0 !important', borderRadius: '1px' },
  '& .MuiOutlinedInput-root': { borderRadius: '1px' },
};

function InlineText({ value, onChange, onBlur, placeholder, type = 'text', align }) {
  return (
    <TextField variant="outlined" size="small" type={type}
      value={value} placeholder={placeholder}
      sx={{ ...inputSx, width: '100%', '& input': { textAlign: align || 'left' } }}
      onChange={e => onChange(e.target.value)}
      onBlur={onBlur}
      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') e.target.blur(); }}
      autoFocus
    />
  );
}

function InlineSelect({ value, onChange, onBlur, options, placeholder }) {
  return (
    <Select size="small" value={value} displayEmpty
      sx={{ fontSize: 12, width: '100%', height: 24,
        '& .MuiSelect-select': { py: '1px', px: '4px', fontSize: 12 },
        '& fieldset': { border: '2px solid #1565c0 !important', borderRadius: '1px' },
      }}
      onChange={e => onChange(e.target.value)}
      onBlur={onBlur}
      autoFocus
    >
      <MenuItem value="" sx={{ fontSize: 12 }}><em>{placeholder || '—'}</em></MenuItem>
      {options.map(o => <MenuItem key={o.id} value={o.id} sx={{ fontSize: 12 }}>{o.name}</MenuItem>)}
    </Select>
  );
}

// ─── Журнал ──────────────────────────────────────────────
const COL_WIDTHS = { date: 90, from: 150, amt_in: 105, to: 150, amt_out: 105, purpose: 150, comment: 'auto', balance: 110, del: 28 };

function JournalTab({ purposes, parties, initialBalance }) {
  const today = new Date();
  const [month, setMonth] = useState(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editCell, setEditCell] = useState(null);
  const [newRow, setNewRow] = useState(null);
  const saveTimer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/finance/transactions/?month=${month}&ordering=date,id`);
      setTxns(r.data?.results || r.data || []);
    } finally { setLoading(false); }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const saveField = (id, field, value) => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const payload = { [field]: value === '' ? null : value };
      if ((field === 'amount_in' || field === 'amount_out') && value !== '')
        payload[field] = Number(value);
      if (field === 'purpose') payload[field] = value || null;
      await api.patch(`/finance/transactions/${id}/`, payload);
      setEditCell(null);
      load();
    }, 80);
  };

  const saveNewRow = async () => {
    if (!newRow) return;
    if (!newRow.date && !newRow.from_party && !newRow.to_party && !newRow.amount_in && !newRow.amount_out) {
      setNewRow(null); return;
    }
    await api.post('/finance/transactions/', {
      date: newRow.date || new Date().toISOString().slice(0, 10),
      order_number: newRow.order_number || '',
      from_party: newRow.from_party || '',
      amount_in: newRow.amount_in ? Number(newRow.amount_in) : null,
      to_party: newRow.to_party || '',
      amount_out: newRow.amount_out ? Number(newRow.amount_out) : null,
      purpose: newRow.purpose || null,
      comment: newRow.comment || '',
    });
    setNewRow(null);
    load();
  };

  const addRow = () => setNewRow({
    date: `${month}-01`, order_number: '', from_party: '',
    amount_in: '', to_party: '', amount_out: '', purpose: '', comment: '',
  });

  const del = async (id) => { await api.delete(`/finance/transactions/${id}/`); load(); };

  let runBalance = initialBalance || 0;
  const withBalance = txns.map(t => {
    runBalance += Number(t.amount_in || 0) - Number(t.amount_out || 0);
    return { ...t, _balance: runBalance };
  });

  const totalIn = txns.reduce((s, t) => s + Number(t.amount_in || 0), 0);
  const totalOut = txns.reduce((s, t) => s + Number(t.amount_out || 0), 0);
  const finalBalance = (initialBalance || 0) + totalIn - totalOut;

  const isEditing = (id, field) => editCell?.id === id && editCell?.field === field;
  const purposeMap = Object.fromEntries(purposes.map(p => [p.id, p.name]));

  // Excel-style cell renderer
  const xcell = (t, field, opts = {}) => {
    const val = t[field] ?? '';
    if (isEditing(t.id, field)) {
      if (opts.type === 'select') {
        return <InlineSelect value={String(val || '')} options={purposes}
          onChange={v => setTxns(rows => rows.map(r => r.id === t.id ? { ...r, [field]: v } : r))}
          onBlur={() => saveField(t.id, field, val)} />;
      }
      return <InlineText value={String(val)} type={opts.type || 'text'} align={opts.align}
        onChange={v => setTxns(rows => rows.map(r => r.id === t.id ? { ...r, [field]: v } : r))}
        onBlur={() => saveField(t.id, field, val)} />;
    }
    const display = field === 'purpose' ? (purposeMap[val] || '') : String(val || '');
    return (
      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        textAlign: opts.align || 'left', color: display ? 'inherit' : 'text.disabled', lineHeight: '22px' }}
        onClick={() => setEditCell({ id: t.id, field })}>
        {display || <span style={{ opacity: 0.3, fontSize: 11 }}>{opts.ph || ''}</span>}
      </Box>
    );
  };

  const thSx = { ...xlCell, bgcolor: XL_HEADER_BG, fontWeight: 700, userSelect: 'none', position: 'sticky', top: 0, zIndex: 2, cursor: 'default' };
  const tdSx = (even, extra) => ({ ...xlCell, bgcolor: even ? XL_EVEN_BG : '#fff', '&:hover': { bgcolor: '#e3eaf6' }, ...extra });
  const numIn = { color: '#1a6b2e', fontWeight: 600, textAlign: 'right' };
  const numOut = { color: '#b71c1c', fontWeight: 600, textAlign: 'right' };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {loading && <LinearProgress sx={{ mb: '2px' }} />}
      {/* Toolbar */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 1, alignItems: 'center' }}>
        <TextField type="month" label="Місяць" value={month} size="small"
          InputLabelProps={{ shrink: true }} onChange={e => setMonth(e.target.value)}
          sx={{ '& .MuiInputBase-input': { fontSize: 13, py: '5px' } }} />
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={addRow}
          sx={{ fontSize: 12, py: '4px', textTransform: 'none' }}>Новий рядок</Button>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          Надходження: <b style={{ color: '#1a6b2e' }}>{fmt(totalIn)}</b> &nbsp;|&nbsp;
          Оплати: <b style={{ color: '#b71c1c' }}>{fmt(totalOut)}</b> &nbsp;|&nbsp;
          Залишок: <b style={{ color: finalBalance >= 0 ? '#1a6b2e' : '#b71c1c' }}>{fmt(finalBalance)}</b>
        </Typography>
      </Box>

      {/* Table */}
      <Paper variant="outlined" sx={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 220px)', borderRadius: 0, border: XL_BORDER }}>
        <Table size="small" sx={{ borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: 900 }}>
          <colgroup>
            <col style={{ width: COL_WIDTHS.date }} />
            <col style={{ width: COL_WIDTHS.from }} />
            <col style={{ width: COL_WIDTHS.amt_in }} />
            <col style={{ width: COL_WIDTHS.to }} />
            <col style={{ width: COL_WIDTHS.amt_out }} />
            <col style={{ width: COL_WIDTHS.purpose }} />
            <col />
            <col style={{ width: COL_WIDTHS.balance }} />
            <col style={{ width: COL_WIDTHS.del }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell sx={thSx}>Дата</TableCell>
              <TableCell sx={thSx}>Від кого</TableCell>
              <TableCell sx={{ ...thSx, textAlign: 'right', color: '#1a6b2e' }}>Надходження</TableCell>
              <TableCell sx={thSx}>Кому</TableCell>
              <TableCell sx={{ ...thSx, textAlign: 'right', color: '#b71c1c' }}>Оплата</TableCell>
              <TableCell sx={thSx}>Призначення</TableCell>
              <TableCell sx={thSx}>Коментар</TableCell>
              <TableCell sx={{ ...thSx, textAlign: 'right' }}>Залишок</TableCell>
              <TableCell sx={{ ...thSx, px: 0 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {withBalance.map((t, idx) => {
              const even = idx % 2 === 1;
              return (
                <TableRow key={t.id}>
                  <TableCell sx={tdSx(even)}>{xcell(t, 'date', { type: 'date' })}</TableCell>
                  <TableCell sx={tdSx(even)}>{xcell(t, 'from_party', { ph: 'від кого' })}</TableCell>
                  <TableCell sx={tdSx(even, numIn)}>
                    {isEditing(t.id, 'amount_in')
                      ? <InlineText value={String(t.amount_in ?? '')} type="number" align="right"
                          onChange={v => setTxns(rows => rows.map(r => r.id === t.id ? { ...r, amount_in: v } : r))}
                          onBlur={() => saveField(t.id, 'amount_in', t.amount_in)} />
                      : <Box sx={{ textAlign: 'right', lineHeight: '22px', cursor: 'text', color: t.amount_in ? '#1a6b2e' : '#ccc' }}
                          onClick={() => setEditCell({ id: t.id, field: 'amount_in' })}>
                          {t.amount_in ? fmt(t.amount_in) : '—'}
                        </Box>}
                  </TableCell>
                  <TableCell sx={tdSx(even)}>{xcell(t, 'to_party', { ph: 'кому' })}</TableCell>
                  <TableCell sx={tdSx(even, numOut)}>
                    {isEditing(t.id, 'amount_out')
                      ? <InlineText value={String(t.amount_out ?? '')} type="number" align="right"
                          onChange={v => setTxns(rows => rows.map(r => r.id === t.id ? { ...r, amount_out: v } : r))}
                          onBlur={() => saveField(t.id, 'amount_out', t.amount_out)} />
                      : <Box sx={{ textAlign: 'right', lineHeight: '22px', cursor: 'text', color: t.amount_out ? '#b71c1c' : '#ccc' }}
                          onClick={() => setEditCell({ id: t.id, field: 'amount_out' })}>
                          {t.amount_out ? fmt(t.amount_out) : '—'}
                        </Box>}
                  </TableCell>
                  <TableCell sx={tdSx(even)}>{xcell(t, 'purpose', { type: 'select' })}</TableCell>
                  <TableCell sx={tdSx(even)}>{xcell(t, 'comment', { ph: '...' })}</TableCell>
                  <TableCell sx={{ ...tdSx(even), textAlign: 'right', fontWeight: 700, color: t._balance >= 0 ? '#1a6b2e' : '#b71c1c' }}>
                    {fmt(t._balance)}
                  </TableCell>
                  <TableCell sx={{ ...tdSx(even), px: '2px', textAlign: 'center' }}>
                    <IconButton size="small" onClick={() => del(t.id)}
                      sx={{ p: '1px', color: '#bbb', '&:hover': { color: 'error.main' } }}>
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}

            {/* New row */}
            {newRow && (
              <TableRow sx={{ bgcolor: '#e8f0fe', '& td': { border: '1px solid #1565c0' } }}>
                <TableCell sx={{ ...xlCell, bgcolor: '#e8f0fe', px: '2px' }}>
                  <InlineText value={newRow.date} type="date"
                    onChange={v => setNewRow(r => ({ ...r, date: v }))} onBlur={() => {}} />
                </TableCell>
                <TableCell sx={{ ...xlCell, bgcolor: '#e8f0fe', px: '2px' }}>
                  <InlineText value={newRow.from_party} placeholder="від кого"
                    onChange={v => setNewRow(r => ({ ...r, from_party: v }))} onBlur={() => {}} />
                </TableCell>
                <TableCell sx={{ ...xlCell, bgcolor: '#e8f0fe', px: '2px' }}>
                  <InlineText value={newRow.amount_in} type="number" placeholder="0" align="right"
                    onChange={v => setNewRow(r => ({ ...r, amount_in: v }))} onBlur={() => {}} />
                </TableCell>
                <TableCell sx={{ ...xlCell, bgcolor: '#e8f0fe', px: '2px' }}>
                  <InlineText value={newRow.to_party} placeholder="кому"
                    onChange={v => setNewRow(r => ({ ...r, to_party: v }))} onBlur={() => {}} />
                </TableCell>
                <TableCell sx={{ ...xlCell, bgcolor: '#e8f0fe', px: '2px' }}>
                  <InlineText value={newRow.amount_out} type="number" placeholder="0" align="right"
                    onChange={v => setNewRow(r => ({ ...r, amount_out: v }))} onBlur={() => {}} />
                </TableCell>
                <TableCell sx={{ ...xlCell, bgcolor: '#e8f0fe', px: '2px' }}>
                  <InlineSelect value={newRow.purpose} options={purposes} placeholder="призначення"
                    onChange={v => setNewRow(r => ({ ...r, purpose: v }))} onBlur={() => {}} />
                </TableCell>
                <TableCell sx={{ ...xlCell, bgcolor: '#e8f0fe', px: '2px' }}>
                  <InlineText value={newRow.comment} placeholder="коментар"
                    onChange={v => setNewRow(r => ({ ...r, comment: v }))} onBlur={() => {}} />
                </TableCell>
                <TableCell sx={{ ...xlCell, bgcolor: '#e8f0fe' }} />
                <TableCell sx={{ ...xlCell, bgcolor: '#e8f0fe', px: '2px', textAlign: 'center' }}>
                  <IconButton size="small" color="primary" onClick={saveNewRow} sx={{ p: '1px' }}>✓</IconButton>
                </TableCell>
              </TableRow>
            )}

            {/* Totals row */}
            {txns.length > 0 && (
              <TableRow sx={{ bgcolor: XL_HEADER_BG, '& td': { border: XL_BORDER, fontWeight: 700, fontSize: 12 } }}>
                <TableCell sx={{ ...xlCell, bgcolor: XL_HEADER_BG, fontWeight: 700 }}>Разом</TableCell>
                <TableCell sx={{ ...xlCell, bgcolor: XL_HEADER_BG }} />
                <TableCell sx={{ ...xlCell, bgcolor: XL_HEADER_BG, textAlign: 'right', color: '#1a6b2e' }}>{fmt(totalIn)}</TableCell>
                <TableCell sx={{ ...xlCell, bgcolor: XL_HEADER_BG }} />
                <TableCell sx={{ ...xlCell, bgcolor: XL_HEADER_BG, textAlign: 'right', color: '#b71c1c' }}>{fmt(totalOut)}</TableCell>
                <TableCell sx={{ ...xlCell, bgcolor: XL_HEADER_BG }} colSpan={2} />
                <TableCell sx={{ ...xlCell, bgcolor: XL_HEADER_BG, textAlign: 'right', color: finalBalance >= 0 ? '#1a6b2e' : '#b71c1c' }}>{fmt(finalBalance)}</TableCell>
                <TableCell sx={{ ...xlCell, bgcolor: XL_HEADER_BG }} />
              </TableRow>
            )}

            {txns.length === 0 && !newRow && !loading && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 3, color: 'text.secondary', fontSize: 12, border: XL_BORDER }}>
                  Немає транзакцій — натисніть «Новий рядок»
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}

// ─── Дашборд ──────────────────────────────────────────────
function DashboardTab({ year }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/finance/dashboard/?year=${year}`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [year]);

  if (loading) return <LinearProgress sx={{ mt: 2 }} />;
  if (!data) return null;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <StatCard label="Надходження за рік" value={data.total_income} color="success.main" />
        <StatCard label="Оплати за рік" value={data.total_expense} color="error.main" />
        <StatCard label="Залишок на кінець" value={data.final_balance} color={data.final_balance >= 0 ? 'success.dark' : 'error.dark'} />
        <StatCard label="Місяців з касовим розривом" value={null}
          label2={`${data.shortage_months_count} міс.`} />
      </Box>

      <Typography variant="h6" fontWeight={700} mb={1}>Касовий розрив по місяцях</Typography>
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.100' } }}>
              <TableCell>Місяць</TableCell>
              <TableCell align="right">Залишок на 01</TableCell>
              <TableCell align="right">Надходження</TableCell>
              <TableCell align="right">Оплати</TableCell>
              <TableCell align="right">Планова потреба</TableCell>
              <TableCell align="right">Доступно</TableCell>
              <TableCell align="right">Брак коштів</TableCell>
              <TableCell align="center">Статус</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.by_month.map(m => (
              <TableRow key={m.month} hover>
                <TableCell fontWeight={600}>{m.month_name}</TableCell>
                <TableCell align="right">{fmt(m.balance_start)}</TableCell>
                <TableCell align="right" sx={{ color: m.income > 0 ? 'success.main' : 'text.disabled' }}>{fmt(m.income)}</TableCell>
                <TableCell align="right" sx={{ color: m.expense > 0 ? 'error.main' : 'text.disabled' }}>{fmt(m.expense)}</TableCell>
                <TableCell align="right">{fmt(m.budget)}</TableCell>
                <TableCell align="right">{fmt(m.available)}</TableCell>
                <TableCell align="right" sx={{ color: m.shortage > 0 ? 'error.main' : 'text.disabled', fontWeight: m.shortage > 0 ? 700 : 400 }}>
                  {m.shortage > 0 ? fmt(m.shortage) : '—'}
                </TableCell>
                <TableCell align="center">
                  <Chip label={m.status} size="small" color={m.status === 'OK' ? 'success' : 'error'} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {data.by_purpose.length > 0 && (
        <>
          <Typography variant="h6" fontWeight={700} mb={1}>Витрати по призначеннях</Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.100' } }}>
                  <TableCell>Призначення</TableCell><TableCell align="right">Сума, грн</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.by_purpose.map(p => (
                  <TableRow key={p.name} hover>
                    <TableCell>{p.name}</TableCell>
                    <TableCell align="right">{fmt(p.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}

// ─── Планова потреба ──────────────────────────────────────
function BudgetTab({ year, purposes }) {
  const [budgets, setBudgets] = useState([]);
  const [initialBalance, setInitialBalance] = useState('');
  const [settingsId, setSettingsId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState({}); // {purposeId_month: value}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bRes, sRes] = await Promise.all([
        api.get(`/finance/budgets/?year=${year}`),
        api.get(`/finance/settings/?year=${year}`),
      ]);
      setBudgets(bRes.data?.results || bRes.data || []);
      const s = (sRes.data?.results || sRes.data || [])[0];
      setInitialBalance(s ? s.initial_balance : '');
      setSettingsId(s ? s.id : null);
    } finally { setLoading(false); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const getBudget = (purposeId, month) => {
    const key = `${purposeId}_${month}`;
    if (editing[key] !== undefined) return editing[key];
    const b = budgets.find(b => b.purpose === purposeId && b.month === month);
    return b ? b.planned_amount : '';
  };

  const saveBudgetCell = async (purposeId, month, value) => {
    const key = `${purposeId}_${month}`;
    setEditing(e => { const n = { ...e }; delete n[key]; return n; });
    const num = parseFloat(value) || 0;
    const existing = budgets.find(b => b.purpose === purposeId && b.month === month);
    if (existing) {
      await api.patch(`/finance/budgets/${existing.id}/`, { planned_amount: num });
    } else {
      await api.post('/finance/budgets/', { year, month, purpose: purposeId, planned_amount: num });
    }
    load();
  };

  const saveInitialBalance = async () => {
    if (settingsId) {
      await api.patch(`/finance/settings/${settingsId}/`, { initial_balance: parseFloat(initialBalance) || 0 });
    } else {
      await api.post('/finance/settings/', { year, initial_balance: parseFloat(initialBalance) || 0 });
    }
    load();
  };

  const rowTotal = (purposeId) => {
    let sum = 0;
    for (let m = 1; m <= 12; m++) {
      const b = budgets.find(b => b.purpose === purposeId && b.month === m);
      sum += b ? Number(b.planned_amount) : 0;
    }
    return sum;
  };

  return (
    <Box>
      {loading && <LinearProgress sx={{ mb: 1 }} />}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField label="Початковий залишок року, грн" type="number" value={initialBalance}
          size="small" onChange={e => setInitialBalance(e.target.value)}
          onBlur={saveInitialBalance} sx={{ width: 280 }} />
        <Typography variant="body2" color="text.secondary">
          (натисніть Tab або клікніть поза полем для збереження)
        </Typography>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.100' } }}>
              <TableCell sx={{ minWidth: 160 }}>Призначення</TableCell>
              {MONTHS.map(m => <TableCell key={m} align="right" sx={{ minWidth: 80 }}>{m}</TableCell>)}
              <TableCell align="right" sx={{ minWidth: 90 }}>Рік</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {purposes.map(p => (
              <TableRow key={p.id} hover>
                <TableCell sx={{ fontWeight: 600 }}>{p.name}</TableCell>
                {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => {
                  const key = `${p.id}_${m}`;
                  const val = getBudget(p.id, m);
                  return (
                    <TableCell key={m} align="right" sx={{ p: 0.5 }}>
                      <TextField
                        type="number" size="small" variant="standard"
                        value={editing[key] !== undefined ? editing[key] : (val || '')}
                        placeholder="0"
                        inputProps={{ style: { textAlign: 'right', fontSize: 13 }, min: 0 }}
                        sx={{ width: 75 }}
                        onChange={e => setEditing(ed => ({ ...ed, [key]: e.target.value }))}
                        onBlur={e => saveBudgetCell(p.id, m, e.target.value)}
                      />
                    </TableCell>
                  );
                })}
                <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(rowTotal(p.id))}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ─── Звіти ──────────────────────────────────────────────
function ReportsTab({ year }) {
  const [data, setData] = useState(null);
  const [subTab, setSubTab] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/finance/reports/?year=${year}`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [year]);

  if (loading) return <LinearProgress sx={{ mt: 2 }} />;
  if (!data) return null;

  const renderTable = (obj) => {
    const rows = Object.entries(obj);
    if (rows.length === 0) return <Typography color="text.secondary" sx={{ mt: 2 }}>Немає даних</Typography>;
    return (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.100' } }}>
              <TableCell sx={{ minWidth: 180 }}>Назва</TableCell>
              {MONTHS.map(m => <TableCell key={m} align="right">{m}</TableCell>)}
              <TableCell align="right">Рік</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(([name, vals]) => {
              const total = vals.reduce((a, v) => a + v, 0);
              return (
                <TableRow key={name} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{name}</TableCell>
                  {vals.map((v, i) => (
                    <TableCell key={i} align="right" sx={{ color: v > 0 ? 'text.primary' : 'text.disabled' }}>
                      {v > 0 ? fmt(v) : '—'}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(total)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const datasets = [
    { label: 'Надходження', obj: data.by_party_income },
    { label: 'Оплати', obj: data.by_party_expense },
    { label: 'По призначеннях', obj: data.by_purpose },
    { label: 'По замовленнях', obj: data.by_order },
  ];

  return (
    <Box>
      <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ mb: 2 }}>
        {datasets.map(d => <Tab key={d.label} label={d.label} />)}
      </Tabs>
      {renderTable(datasets[subTab].obj)}
    </Box>
  );
}

// ─── Main ──────────────────────────────────────────────
export default function FinancePage() {
  const currentYear = new Date().getFullYear();
  const [tab, setTab] = useState(0);
  const [year, setYear] = useState(currentYear);
  const [purposes, setPurposes] = useState([]);
  const [parties, setParties] = useState([]);

  useEffect(() => {
    api.get('/finance/purposes/').then(r => setPurposes(r.data?.results || r.data || []));
    api.get('/finance/parties/').then(r => setParties(r.data?.results || r.data || []));
  }, []);

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography variant="h6" fontWeight={700} sx={{ fontSize: 16 }}>Cash Flow</Typography>
        <TextField
          label="Рік" type="number" size="small" value={year}
          onChange={e => setYear(Number(e.target.value))}
          inputProps={{ min: 2020, max: 2035, style: { width: 60, fontSize: 13 } }}
          sx={{ '& .MuiInputBase-input': { py: '5px' } }}
        />
      </Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)}
        sx={{ mb: 1.5, minHeight: 36, borderBottom: 1, borderColor: 'divider',
          '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: 13, textTransform: 'none' } }}>
        <Tab label="Журнал" />
        <Tab label="Дашборд" />
        <Tab label="Планова потреба" />
        <Tab label="Звіти" />
      </Tabs>
      {tab === 0 && <JournalTab purposes={purposes} parties={parties} />}
      {tab === 1 && <DashboardTab year={year} />}
      {tab === 2 && <BudgetTab year={year} purposes={purposes} />}
      {tab === 3 && <ReportsTab year={year} />}
    </Box>
  );
}
